import { PlayerEvent as PublicPlayerEvent } from '@foundation-player/runtime';

/**
 * {@inheritDoc PlayerEvent}
 */
export const PlayerEvent = {
  ...PublicPlayerEvent,

  /**
   * The player has parsed new metadata.
   *
   * @remarks
   * This event is highly volatile and may change a lot between versions and players.
   *
   * @internal
   */
  UpdateMetaData: 'player:UpdateMetaData',

  /**
   * ID3v2 or EMSG Metadata emitted as an event timed accordingly to its playback.
   *
   * @internal
   */
  TimedMetadata: 'player:TimedMetadata',

  /**
   * Triggered every time a new metric is added.
   *
   * @internal
   */
  MetricAdded: 'player:MetricAdded',

  /**
   * The player received the manifest and accepted it.
   *
   * @remarks
   * This event is emitted after the player adapter has loaded the manifest and the respective load method resolved.
   * This does **not** mean that the content can be played successfully. It may still fail due to DRM or other reasons.
   *
   * @internal
   *
   * TODO: visibility changed to internal; check if used in apps
   */
  PlayerLoaded: 'player:PlayerLoaded',

  /**
   * Player starts loading a new media.
   *
   * @remarks
   * This event is emitted when the player hands over the media to the player adapter.
   * The player adapter will then start loading the media.
   *
   * @internal
   *
   * TODO: visibility changed to internal; check if used in apps
   */
  MediaLoad: 'player:MediaLoad',

  /**
   * Media is loaded and passed loaded checks in the player adapter.
   *
   * @remarks
   * This event is emitted by the PlayerAdapter specifically, not the root player.
   *
   * This happens at different times in the lifecycle of the playout process, which may vary between player- and content types,
   * but is generally linked to the 'ContentReady' Event. Notable exceptions are documented in the player adapters.
   *
   * When developing lifecycle logic, it is recommended to listen this event instead of `ContentReady`.
   *
   * @internal
   *
   * TODO: visibility changed to internal; check if used in apps
   */
  MediaLoaded: 'player:MediaLoaded',

  /**
   * Player is destroyed and must not be used anymore.
   *
   * @remarks
   * This happens after the {@link PlayerEvent.Destroy} event, once all tear down operations are finished.
   *
   * This is the last event emitted by the player, because all event listeners are removed before this event is emitted.
   *
   * @internal
   *
   * TODO: visibility changed to internal; check if used in apps
   */
  Destroyed: 'player:Destroyed',

  /**
   * The player has parsed new metadata.
   *
   * @remarks
   * This event is highly volatile and may change a lot between versions and players.
   *
   * @internal
   */
  Metadata: 'player:Metadata',

  /**
   * The playback has finished. Use ContentEnd or SessionEnd instead.
   *
   * @internal
   */
  PlaybackFinished: 'player:PlaybackFinished',

  /**
   * Content is ready for playback
   * @internal
   */
  ContentReady: 'player:ContentReady',

  /**
   * The player loaded the manifest. Use Ready event instead for the UI state.
   *
   * @internal
   */
  SourceLoaded: 'player:SourceLoaded',

  /**
   * The current media source was removed.
   *
   * @internal
   */
  SourceUnloaded: 'player:SourceUnloaded',

  /**
   * Player will retry playback after stalling was detected.
   * Stalling can happen with slow / bad network or temporary limited system resources.
   *
   * @internal
   */
  StallingFix: 'player:StallingFix',

  /**
   * MediaAdaptation event is emitted often, even though there was no change in it
   *
   * @internal
   *
   * TODO: remove it, because it's not used anymore?
   */
  MediaAdaptation: 'player:MediaAdaptation',

  /**
   * The player successfully was able to start a video
   * using autoplay. This event is fired after a couple of
   * seconds of video play.
   */
  AutoplaySuccessful: 'player:AutoplaySuccessful',

  /**
   * All other track changes, that are not further mapped.
   *
   * @internal
   */
  TrackChanged: 'player:TrackChanged',

  /**
   * Fired when the player has selected a key system.
   *
   * @internal
   */
  KeySystemSelected: 'player:KeySystemSelected',

  /**
   * Fired when an error was actively suppressed by the player e.g. due to a fallback.
   * Suppressed errors will not be reported to the user, but still logged in analytics.
   *
   * @internal
   */
  SuppressedError: 'player:SuppressedError',
} as const;

export type PlayerEvent = (typeof PlayerEvent)[keyof typeof PlayerEvent];
